import React from 'react';
import { EditableName } from 'apps/sigma2-ui/src/components/dashboards/EditableName';
import { DashboardDraftState } from 'apps/sigma2-ui/src/lib/hooks/useDashboards';
import { PageHeader } from 'apps/sigma2-ui/src/components/page-header/PageHeader';
import { PageHeaderBackButton } from 'apps/sigma2-ui/src/components/page-header/PageHeaderBackButton';
import { PageHeaderBadge } from 'apps/sigma2-ui/src/components/page-header/PageHeaderBadge';
import { PublicIndicatorPill } from 'apps/sigma2-ui/src/components/dashboards/PublicIndicatorPill';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { PublishDashboardModal } from 'apps/sigma2-ui/src/components/dashboards/PublishModal';
import { PrimaryButtonLightBg } from 'apps/sigma2-ui/src/components/PrimaryButtonLightBg';
import { CircularProgress } from '@mui/material';
import { DashboardHeaderShareButton } from 'apps/sigma2-ui/src/components/dashboards/DashboardHeaderShareButton';

export const DashboardDetailsHeader: React.FC<{
  dashboardDraft: DashboardDraftState | null;
  isEmbedMode: boolean;
  onAfterSave: (name: string) => void;
  showPublishDashboardButton: boolean;
  onSave: () => void;
  savePending: boolean;
  showDownloadMultiAnalysisButton: boolean;
  canDownloadDashboard: boolean;
  onExport: (onComplete?: () => void) => void;
  exportPending?: boolean;
  summaryControl?: React.ReactNode;
  menu?: React.ReactNode;
}> = ({
  dashboardDraft,
  isEmbedMode,
  onAfterSave,
  showPublishDashboardButton,
  onSave,
  savePending,
  showDownloadMultiAnalysisButton,
  canDownloadDashboard,
  onExport,
  exportPending = false,
  summaryControl,
  menu,
}) => {
  const language = useLanguage();
  const hasUnsavedChanges =
    dashboardDraft?.dirty === true ||
    dashboardDraft?.summaryIntentDirty === true;

  return (
    <PageHeader
      dataTestId="dashboard-header"
      contentClassName="min-[1300px]:flex-nowrap"
      leftClassName="max-[1299px]:basis-full"
      left={
        <div className="flex min-w-0 items-center gap-4">
          {!isEmbedMode && (
            <PageHeaderBackButton
              behavior={{
                type: 'href',
                href: '/dashboards',
              }}
            />
          )}
          <div className="w-fit max-[1299px]:max-w-none min-[1300px]:max-w-[240px] min-[1440px]:max-w-none">
            <EditableName
              name={dashboardDraft?.draft.name ?? ''}
              size="headline-5"
              onAfterSave={onAfterSave}
              className="w-full"
            />
          </div>
          {!isEmbedMode && dashboardDraft?.draft.isPublic && (
            <PublicIndicatorPill collapseToIndicatorBelow1240 />
          )}
        </div>
      }
      center={
        !isEmbedMode ? (
          <>
            {summaryControl}
            {dashboardDraft?.record && (
              <PageHeaderBadge
                label={language.lastEditedLabel()}
                value={language.relativeTimeShort({
                  date: dashboardDraft.record.updatedAt,
                })}
                labelVariant="body-6-medium"
                valueVariant="body-6"
              />
            )}
            <S2Typography
              translatedContent={language.dashboardsUnsavedChangesLabel()}
              variant="body-7"
              aria-hidden={!hasUnsavedChanges}
              dangerouslyOverrideClassNameFn={(cn) =>
                `${cn} max-[1599px]:hidden ${hasUnsavedChanges ? '' : 'hidden'}`
              }
            />
          </>
        ) : undefined
      }
      centerClassName="min-[1300px]:flex-nowrap overflow-visible"
      right={
        !isEmbedMode ? (
          <>
            {showPublishDashboardButton && dashboardDraft && (
              <PublishDashboardModal
                dashboardId={dashboardDraft.dashboardId}
                dashboard={dashboardDraft.draft}
                dashboardOwnerEmail={dashboardDraft.record?.ownerEmail ?? null}
                showDownloadDashboardButton={showDownloadMultiAnalysisButton}
                canDownloadDashboard={canDownloadDashboard}
                onDownloadDashboard={onExport}
                downloadPending={exportPending}
                renderTrigger={({ onClick }) => (
                  <DashboardHeaderShareButton
                    behavior={{
                      type: 'onClick',
                      onClick,
                    }}
                  />
                )}
              />
            )}
            {menu}
            <PrimaryButtonLightBg
              translatedContent={language.dashboardsSaveButtonLabel({
                isPublic: Boolean(dashboardDraft?.draft.isPublic),
              })}
              disabled={
                !dashboardDraft?.dirty ||
                savePending ||
                dashboardDraft?.summaryIntentDirty === true
              }
              onClick={onSave}
              icon={() =>
                savePending ? <CircularProgress size={16} /> : undefined
              }
            />
          </>
        ) : undefined
      }
      rightClassName="min-[1300px]:flex-nowrap shrink-0 overflow-visible"
    />
  );
};
